import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { Empty, Loading, apiError, monthOf, saveBlob, ui, useCampus, useIdleLocked } from "../kits/shared";

/*
	Tienda → Maestros (sección "teacherBoard", fase 2, 2026-10-01): los maestros como vendedores.
	Tablero por maestro (alumnos activos con kit, kits del mes de su horario o vendidos por él, incentivo, tendencia, semáforo)
	y Horarios sin kit (grupos donde menos de la mitad compró).
*/

const pctText = (value: number | null | undefined) => value === null || value === undefined ? "-" : value.toFixed(1) + "%";
/*
	Semáforo sin rojo (diseño WAPP): verde lleno = va bien, aro oro = cerca de la meta, oro lleno = bajo.
	Nunca un color "-suave" en un indicador: se pierde contra el fondo.
*/
const LIGHT: Record<string, React.CSSProperties> = {
	green: { background: "var(--wa-verde)", border: "2px solid var(--wa-verde)" },
	yellow: { background: "transparent", border: "2px solid var(--wa-oro)" },
	red: { background: "var(--wa-oro)", border: "2px solid var(--wa-oro)" }
};
const LIGHT_TITLE: Record<string, string> = { green: "En la meta", yellow: "Cerca de la meta", red: "Bajo la meta" };

const Maestros: React.FC = () => {
	const { campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const http = HttpManager.getInstance().salesService;
	const [tab, setTab] = useState<"board" | "schedules">("board");
	const [month, setMonth] = useState<string>(monthOf());
	const [board, setBoard] = useState<any | null>(null);
	const [schedules, setSchedules] = useState<any[] | null>(null);
	const [onlyLow, setOnlyLow] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const money = (value: number | null | undefined) => value === null || value === undefined ? "-" : currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

	useEffect(() => {
		if (!campusId) return;
		setError(null);
		if (tab === "board") {
			setBoard(null);
			http.teacherBoard(campusId, month).then((response) => setBoard(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo cargar.")));
		} else {
			setSchedules(null);
			http.teacherSchedules(campusId).then((response) => setSchedules(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo cargar.")));
		}
	}, [campusId, tab, month]);

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;
	const shown = (schedules || []).filter((row) => !onlyLow || row.low);
	const monthShort = (value: string) => ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"][Number(value.slice(5, 7)) - 1] || value;

	return <div style={ui.page}>
		<h2 style={ui.title}>Maestros</h2>
		<div style={ui.tabs}>
			<button type="button" disabled={idleLocked} className={"btn rounded-pill " + (tab === "board" ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab("board")}>Tablero</button>
			<button type="button" disabled={idleLocked} className={"btn rounded-pill " + (tab === "schedules" ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTab("schedules")}>
				Horarios sin kit{board?.totals?.lowSchedules ? <span style={ui.badge(tab === "schedules")}>{board.totals.lowSchedules}</span> : null}</button>
			<button type="button" className={ui.btn} style={{ marginLeft: "auto" }} disabled={idleLocked}
				onClick={() => { setError(null); http.teacherExcel(campusId, month).then((response) => saveBlob(response.data, "Maestros_" + month + ".xlsx", "Ventas de tienda")).catch((requestError) => setError(apiError(requestError, "No se pudo generar el Excel."))); }}>Excel</button>
		</div>
		{error ? <div className="alert alert-danger">{error}</div> : null}
		{tab === "board" ? <>
			<div style={{ ...ui.card, ...ui.filters }}>
				<div style={ui.field("150px")}><span style={ui.caption}>Mes</span>
					<input type="month" className="form-control" style={ui.input} value={month} disabled={idleLocked} onChange={(event) => setMonth(event.target.value)} /></div>
				{board ? <span style={ui.small}>Meta del plantel {board.goalKitRate !== null ? pctText(board.goalKitRate) : "sin meta (90%)"} · incentivo {String(board.incentiveStatus || "").toLowerCase()}</span> : null}
			</div>
			{board ? <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "10px 0" }}>
				<div style={ui.kpi}><div style={ui.caption}>Alumnos activos con kit</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{pctText(board.totals.kitRate)}</div><div style={ui.tiny}>{board.totals.withKit} de {board.totals.students}</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Kits del mes por maestros</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{board.totals.kits}</div><div style={ui.tiny}>{board.totals.teachers} maestros</div></div>
				<div style={ui.kpi}><div style={ui.caption}>Horarios con menos de la mitad con kit</div><div style={{ fontSize: "20px", fontWeight: 700, color: board.totals.lowSchedules ? "var(--wa-oro)" : undefined }}>{board.totals.lowSchedules}</div></div>
			</div> : null}
			<div className="waScrollX" style={{ ...ui.card, overflowX: "auto" }}>
				{board === null ? <Loading /> : board.teachers.length < 1 ? <Empty text="Sin maestros con grupos activos." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>#</th><th style={ui.th}>Maestro</th><th style={{ ...ui.th, ...ui.right }}>Horarios</th><th style={{ ...ui.th, ...ui.right }}>Alumnos activos</th>
						<th style={{ ...ui.th, ...ui.right }}>Con kit</th><th style={{ ...ui.th, ...ui.right }}>Kits del mes</th><th style={{ ...ui.th, ...ui.right }}>Otros</th>
						<th style={{ ...ui.th, ...ui.right }}>Cobrado</th><th style={{ ...ui.th, ...ui.right }}>Incentivo</th><th style={ui.th}>Últimos 3 meses</th>
					</tr></thead>
					<tbody>{board.teachers.map((teacher: any, index: number) => <tr key={teacher.userId}>
						<td style={ui.td}>{index + 1}</td>
						<td style={ui.td}><b>{teacher.name}</b>{teacher.direct ? <div style={ui.tiny}>vendió él {teacher.direct}</div> : null}{teacher.lowSchedules ? <div style={{ ...ui.tiny, color: "var(--wa-oro)" }}>{teacher.lowSchedules} horario(s) sin kit</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right }}>{teacher.schedules}</td>
						<td style={{ ...ui.td, ...ui.right }}>{teacher.students}</td>
						<td style={{ ...ui.td, ...ui.right, whiteSpace: "nowrap" }}>
							<span title={teacher.light ? LIGHT_TITLE[teacher.light] : undefined} style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", boxSizing: "border-box", verticalAlign: "middle", marginRight: "6px", ...(teacher.light && LIGHT[teacher.light] ? LIGHT[teacher.light] : { background: "var(--wa-filo)", border: "2px solid var(--wa-filo)" }) }} />
							<b>{pctText(teacher.kitRate)}</b> <span style={ui.tiny}>{teacher.withKit}</span></td>
						<td style={{ ...ui.td, ...ui.right, fontWeight: 600 }}>{teacher.kits}</td>
						<td style={{ ...ui.td, ...ui.right }}>{teacher.others}</td>
						<td style={{ ...ui.td, ...ui.right }}>{money(teacher.amount)}</td>
						<td style={{ ...ui.td, ...ui.right }}>{teacher.incentive ? money(teacher.incentive) : "-"}</td>
						<td style={{ ...ui.td, whiteSpace: "nowrap" }}>{teacher.trend.map((point: any) => <span key={point.month} style={{ ...ui.small, marginRight: "8px" }}>{monthShort(point.month)} <b style={{ color: "var(--wa-tinta)" }}>{point.kits}</b></span>)}</td>
					</tr>)}</tbody>
				</table>}
			</div>
		</> : <>
			<div style={{ ...ui.card, ...ui.filters }}>
				<div onClick={() => setOnlyLow(!onlyLow)} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px" }}>
					<input type="checkbox" checked={onlyLow} readOnly /> Solo menos de la mitad con kit
				</div>
				{schedules ? <span style={{ ...ui.small, marginLeft: "auto" }}>{shown.length} de {schedules.length} horarios activos</span> : null}
			</div>
			<div className="waScrollX" style={{ ...ui.card, overflowX: "auto", marginTop: "10px" }}>
				{schedules === null ? <Loading /> : shown.length < 1 ? <Empty text="Todos los horarios activos tienen al menos la mitad con kit." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr><th style={ui.th}>Horario</th><th style={ui.th}>Curso</th><th style={ui.th}>Periodo</th><th style={ui.th}>Maestro</th>
						<th style={{ ...ui.th, ...ui.right }}>Alumnos activos</th><th style={{ ...ui.th, ...ui.right }}>Con kit</th><th style={{ ...ui.th, ...ui.right }}>%</th></tr></thead>
					<tbody>{shown.map((row: any) => <tr key={row.scheduleId}>
						<td style={ui.td}><b>{row.code}</b></td>
						<td style={ui.td}>{row.course}{row.module && row.module !== row.course ? <div style={ui.tiny}>{row.module}</div> : null}</td>
						<td style={ui.td}>{row.period}</td>
						<td style={ui.td}>{row.mentor || "-"}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.students}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.withKit}</td>
						<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: row.low ? "var(--wa-oro)" : "var(--wa-verde)" }}>{pctText(row.kitRate)}</td>
					</tr>)}</tbody>
				</table>}
			</div>
		</>}
	</div>;
};

export default Maestros;
